import {
    WebsiteDiagnostics,
    PageableResult,
    PageableSearchOptions,
    Resource,
    ScreenDto,
    UpdateScreenConfigurationDto,
    LedControllerCommandResponsePayload,
    LedControllerPairRequestDto,
} from "@exibra/shared";
import { ApiService } from "../api/api.service";

class ScreenService extends ApiService {
    public async getWebsiteDiagnostics(
        id: number,
        screenshot: boolean,
    ): Promise<WebsiteDiagnostics | null> {
        const response = await this.axios.get<WebsiteDiagnostics | null>(
            `${Resource.Screen}/${id}/website-diagnostics`,
            {
                params: { screenshot },
                timeout: 15_000,
            },
        );
        return response.data;
    }

    /**
     * Gets a screen by its serial number
     * @param serialNumber The serial number of the screen
     * @returns The screen info
     */
    public async getScreen(id: number): Promise<ScreenDto> {
        const res = await this.axios.get<ScreenDto>(`${Resource.Screen}/${id}`);

        return res.data;
    }

    /**
     * Gets a list of screens
     * @param options The options to search the screens
     * @returns The list of screens
     */
    public async getScreens(options: PageableSearchOptions): Promise<PageableResult<ScreenDto>> {
        const res = await this.axios.get<PageableResult<ScreenDto>>(`${Resource.Screen}`, {
            params: options,
        });

        return res.data;
    }

    /**
     * Updates a screen's information
     * @param screenId The id of the screen
     * @param data The data to update
     * @returns The updated screen info
     */
    public async updateScreen(screenId: number, data: Partial<ScreenDto>): Promise<ScreenDto> {
        const res = await this.axios.patch<ScreenDto>(`${Resource.Screen}/${screenId}`, data);

        return res.data;
    }

    /**
     * Updates the placeholders for a screen's template
     * @param serialNumber The serial number of the screen
     * @param placeholders The placeholder values to update
     * @returns The updated screen info
     */
    public async updateScreenConfiguration(
        screenId: number,
        data: UpdateScreenConfigurationDto,
    ): Promise<ScreenDto> {
        const res = await this.axios.post<ScreenDto>(
            `${Resource.Screen}/${screenId}/configure`,
            data,
        );

        return res.data;
    }

    /**
     * Triggers an immediate presenter update for a screen.
     * @param screenId The id of the screen whose presenter should update
     * @returns The accepted update request
     */
    public async triggerPresenterUpdate(
        screenId: number,
    ): Promise<{ requestId: string; screenId: number; uuid: string }> {
        const res = await this.axios.post<{ requestId: string; screenId: number; uuid: string }>(
            `${Resource.Screen}/${screenId}/presenter-update`,
        );

        return res.data;
    }

    /**
     * Reads the LED controller state reported by an online presenter.
     * @param screenId The id of the screen whose presenter should report status
     * @returns The acknowledged LED controller command response
     */
    public async getLedControllerStatus(
        screenId: number,
    ): Promise<LedControllerCommandResponsePayload> {
        const res = await this.axios.get<LedControllerCommandResponsePayload>(
            `${Resource.Screen}/${screenId}/led-controller/status`,
        );

        return res.data;
    }

    /**
     * Scans for LEDnet controllers visible to an online presenter.
     * @param screenId The id of the screen whose presenter should scan
     * @returns The acknowledged LED controller command response
     */
    public async scanLedControllers(
        screenId: number,
    ): Promise<LedControllerCommandResponsePayload> {
        const res = await this.axios.get<LedControllerCommandResponsePayload>(
            `${Resource.Screen}/${screenId}/led-controller/scan`,
        );

        return res.data;
    }

    /**
     * Pairs an online presenter with one LEDnet controller.
     * @param screenId The id of the screen whose presenter should pair
     * @param pairing The selected SSID/BSSID pairing target
     * @returns The acknowledged LED controller command response
     */
    public async pairLedController(
        screenId: number,
        pairing: LedControllerPairRequestDto,
    ): Promise<LedControllerCommandResponsePayload> {
        const res = await this.axios.post<LedControllerCommandResponsePayload>(
            `${Resource.Screen}/${screenId}/led-controller/pair`,
            pairing,
        );

        return res.data;
    }

    /**
     * Triggers a transient locate (visual identify) on a selected unpaired LED controller.
     * @param screenId The id of the screen whose presenter should locate
     * @param target The selected SSID/BSSID locate target
     * @returns The acknowledged LED controller command response
     */
    public async locateLedController(
        screenId: number,
        target: LedControllerPairRequestDto,
    ): Promise<LedControllerCommandResponsePayload> {
        const res = await this.axios.post<LedControllerCommandResponsePayload>(
            `${Resource.Screen}/${screenId}/led-controller/locate`,
            target,
        );

        return res.data;
    }

    /**
     * Clears an online presenter's local LED controller pairing.
     * @param screenId The id of the screen whose presenter should forget pairing
     * @returns The acknowledged LED controller command response
     */
    public async forgetLedController(
        screenId: number,
    ): Promise<LedControllerCommandResponsePayload> {
        const res = await this.axios.delete<LedControllerCommandResponsePayload>(
            `${Resource.Screen}/${screenId}/led-controller/pairing`,
        );

        return res.data;
    }

    /**
     * Deletes multiple screens by their serial numbers
     * @param serialNumbers Array of serial numbers of screens to delete
     * @returns void
     */
    public async deleteScreens(serialNumbers: string[]): Promise<void> {
        // Delete screens one by one since the API expects single screen deletion
        await Promise.all(
            serialNumbers.map((serialNumber) =>
                this.axios.delete(`${Resource.Screen}/${encodeURIComponent(serialNumber)}`),
            ),
        );
    }

    /**
     * Validates a serial number
     * @param serialNumber The serial number to validate
     * @returns Whether the serial number is valid
     */
    public async validateSerialNumber(serialNumber: string): Promise<boolean> {
        const res = await this.axios.get<boolean>(
            `${Resource.Screen}/serial-number/validate?serialNumber=${serialNumber}`,
        );

        return res.data;
    }

    /**
     * Pairs a screen with a tenant
     * @param uuid The serial number of the screen
     * @param tenantId The ID of the tenant to pair with
     * @returns The updated screen info
     */
    public async pairScreen(
        uuid: string,
        tenantId: number,
        name: string,
        serialNumber: string,
    ): Promise<ScreenDto> {
        const res = await this.axios.patch<ScreenDto>(
            `${Resource.Screen}/${encodeURIComponent(uuid)}/pair`,
            { tenantId, name, serialNumber },
        );

        return res.data;
    }

    /**
     * Sends a locate event to a screen to make it visually identifiable
     * @param serialNumber The serial number of the screen to locate
     * @returns The screen info
     */
    public async locateScreen(serialNumber: string): Promise<ScreenDto> {
        const res = await this.axios.post<ScreenDto>(
            `${Resource.Screen}/${encodeURIComponent(serialNumber)}/locate`,
        );

        return res.data;
    }

    /**
     * Check if a screen is currently in locate mode
     * @param serialNumber The serial number of the screen to check
     * @returns Whether the screen is in locate mode
     */
    public async isScreenLocating(serialNumber: string): Promise<boolean> {
        const res = await this.axios.get<boolean>(
            `${Resource.Screen}/${encodeURIComponent(serialNumber)}/locate`,
        );

        return res.data;
    }

    /**
     * Gets the next available serial number for a tenant
     * @param tenantId The ID of the tenant
     * @returns The next available serial number
     */
    public async getNextSerialNumber(tenantId: number): Promise<string> {
        const res = await this.axios.get<string>(
            `${Resource.Screen}/next-serial-number?tenantId=${tenantId}`,
        );
        return res.data;
    }
}

export const screenService = new ScreenService();
